import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgPencil from '@icons/pencil-inverted.svg?react';
import SvgTrash from '@icons/delete-trash.svg?react';
import { FilterConfig } from './types';
import { FilterTag } from './FilterTag';
import { FilterChipControl } from './v2/FilterChipControl';
import { getFilterKey, getFilterLabel } from './v2/filterChipUtils';

// Row labels: 14/20 medium, content-primary, ellipsis on overflow (same as the All filters sidebar).
// Font family is inherited from the DM Sans wrapper below — the app never loads Inter.
const LABEL_CLASS = 'min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium leading-5 text-content-primary';

interface SavedFilter<TFilter = any> {
  id: string;
  name: string;
  data: TFilter;
  sort?: { sortField: string; sortDirection: 'ASC' | 'DESC' };
}

interface ManageFiltersSidebarProps<TFilter = any> {
  visible: boolean;
  onClose: () => void;
  savedFilters: SavedFilter<TFilter>[];
  onDelete: (filterId: string) => void;
  /** Fields available for editing — same config the filter bar uses. */
  filtersConf?: FilterConfig[];
  loading?: boolean;
  /** Persist an edited filter (no update API exists, so this is delete-old + recreate). */
  onSaveEdited?: (original: SavedFilter<TFilter>, name: string, data: TFilter) => void;
}

const ManageFiltersSidebar: React.FC<ManageFiltersSidebarProps> = ({ visible, onClose, savedFilters, onDelete, filtersConf = [], loading = false, onSaveEdited }) => {
  const { tr } = useTranslationLgs();

  // When set, the sidebar shows the "All filters" editing layout for this saved filter (on a local draft).
  const [editing, setEditing] = useState<SavedFilter | null>(null);
  const [draft, setDraft] = useState<any>({});
  const [name, setName] = useState('');

  const canEdit = !!onSaveEdited && filtersConf.length > 0;

  const startEdit = (f: SavedFilter) => {
    setEditing(f);
    setDraft({ ...(f.data ?? {}) });
    setName(f.name);
  };
  const cancelEdit = () => setEditing(null);
  const close = () => {
    setEditing(null);
    onClose();
  };
  const applyDraftValue = (key: string, value: any) => setDraft((prev: any) => ({ ...prev, [key]: value }));
  const saveEdit = () => {
    if (editing && name.trim()) {
      onSaveEdited?.(editing, name.trim(), draft);
      setEditing(null);
    }
  };

  return (
    <RingilSidebar
      visible={visible}
      heading={editing ? tr('ManageFiltersSidebar.editFilter', 'Edit filter') : tr('ManageFiltersSidebar.manageFilters', 'Manage filters')}
      onBack={editing ? cancelEdit : close}
      onContinue={editing ? saveEdit : close}
      continueLabel={editing ? tr('SaveFilterSidebar.saveFilter', 'Save filter') : tr('RingilDataTable.closeFilter', 'Close')}
      showBackButton={!!editing}
      disableSaveButton={!!editing && !name.trim()}
      maxWidth="520px"
    >
      {editing ? (
        // Edit layout: same white-row All-filters look, bound to a local draft (no apply to the table).
        <div className="font-['DM_Sans'] flex flex-col items-start self-stretch gap-0.5 p-3">
          <div className="flex w-full flex-col gap-1 bg-white rounded px-3 py-2 mb-3.5">
            <span className={LABEL_CLASS}>{tr('SaveFilterSidebar.filterName', 'Filter name')}</span>
            <InputText value={name} onChange={e => setName(e.target.value)} maxLength={15} className="w-full" data-testid="edit-filter-name" />
          </div>

          {filtersConf.map(conf => (
            <div key={getFilterKey(conf)} className="flex w-full items-center justify-between gap-2 bg-white rounded px-3 py-2">
              <span className={LABEL_CLASS}>{getFilterLabel(conf)}</span>
              <FilterChipControl conf={conf} filter={draft} applyFilterValue={applyDraftValue} loading={loading} wrap hideLabel />
            </div>
          ))}
        </div>
      ) : (
        // List layout: white rows with the saved-filter pill + edit / delete actions.
        <div className="font-['DM_Sans'] flex flex-col items-start self-stretch gap-0.5 p-3">
          {savedFilters.map(filter => (
            <div key={filter.id} className="flex w-full items-center justify-between gap-2 bg-white rounded px-3 py-2">
              {/* Same pill style as the saved-filter bar. */}
              <FilterTag label={filter.name} onClick={canEdit ? () => startEdit(filter) : undefined} />
              <div className="flex shrink-0 items-center gap-1">
                {canEdit && (
                  <RingilButton
                    mode="table"
                    tooltip={tr('ManageFiltersSidebar.editFilter', 'Edit')}
                    icon={<SvgPencil className="w-4 h-4" />}
                    onClick={() => startEdit(filter)}
                    dataTestId={`edit-filter-${filter.id}`}
                  />
                )}
                <RingilButton
                  mode="table"
                  tooltip={tr('ManageFiltersSidebar.deleteFilter', 'Delete')}
                  icon={<SvgTrash className="w-4 h-4" />}
                  onClick={() => onDelete(filter.id)}
                  dataTestId={`delete-filter-${filter.id}`}
                />
              </div>
            </div>
          ))}
          {savedFilters.length === 0 && (
            <div className="w-full py-8 text-center text-content-secondary">{tr('ManageFiltersSidebar.noSavedFilters', 'No saved filters')}</div>
          )}
        </div>
      )}
    </RingilSidebar>
  );
};

export default ManageFiltersSidebar;